import type { Profile } from "@/auth/AuthProvider";

/* Where a member lands after signing in.
 *
 * LEGACY RULE, reproduced (Login.dc.html:356-361):
 *   role Agent + plan Gold | Silver    -> agent panel (tier "gold")
 *   role Agent + plan Platinum         -> agent panel (tier "platinum")
 *   role Agent + any other plan        -> pricing
 *   anything else                      -> member panel
 *
 * TWO DELIBERATE DIFFERENCES, both about where the facts come from:
 *
 * 1. Legacy read role and plan out of `localStorage.ejarmasr_current_user`,
 *    which the browser owner could edit -- the tier-gating hole in inventory
 *    6.7. Here the argument is the profile the SERVER returned for the token,
 *    so editing storage changes nothing.
 *
 * 2. Legacy carried the tier in the URL as `?tier=gold|platinum`, which made
 *    the panel's own gating editable from the address bar. The tier is
 *    derived from the server's plan instead, and no tier appears in the URL.
 *
 * NOT CHANGED, and flagged for Mark: legacy routes on `plan` alone and never
 * checks `verified`, so an unverified Agent on a paid plan reaches the agent
 * panel. That is reproduced rather than silently tightened -- see
 * docs/w4-handoff.md.
 */

const AGENT_PAID_PLANS = ["Gold", "Silver", "Platinum"];

export type AgentTier = "gold" | "platinum";

export function agentTier(plan: string | null | undefined): AgentTier | null {
  if (plan === "Platinum") return "platinum";
  if (plan === "Gold" || plan === "Silver") return "gold";
  return null;
}

export function isAgent(profile: Pick<Profile, "role"> | null | undefined): boolean {
  return (profile?.role ?? "").toLowerCase() === "agent";
}

/** True when this profile may open the agent panel at all. */
export function canUseAgentPanel(profile: Pick<Profile, "role" | "plan"> | null | undefined): boolean {
  return isAgent(profile) && AGENT_PAID_PLANS.includes(profile?.plan ?? "");
}

/** The path to send this profile to after a successful sign-in. */
export function landingPath(profile: Pick<Profile, "role" | "plan"> | null | undefined): string {
  if (!profile) return "/account";
  if (!isAgent(profile)) return "/account";
  return canUseAgentPanel(profile) ? "/agent" : "/pricing";
}
